import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  Phone,
  Mail,
  MapPin,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Users,
  Briefcase,
  ArrowRight,
  Eye,
  Rocket,
  Video,
} from "lucide-react";
import { useNavigate, useNavigation } from "react-router-dom";
import { openExternalLink } from "../../services/site/linkService";
import { InfoService } from "../../services/site/InfoService";
import testVideo from "../../assets/videos/digital.mp4";
import digitalVideo from "../../assets/videos/digital.mp4";
import extractVideo from "../../assets/videos/extract.mp4";
import templateVideo from "../../assets/videos/template.mp4";
import employeeVideo from "../../assets/videos/employee.mp4";
import vendorVideo from "../../assets/videos/vendor.mp4";
import homeVideo from "../../assets/videos/home.mp4";
import HeroSlider from "./HeroSlider";
import "./HeroSlider.scss";
import VideoPlayer from "../../Components/VideoPlayer";
import BookingShowcase from "./BookingShowcase";

const HomePage = () => {
  // ========================================
  // Old HeroSection - Commented out, replaced with HeroSlider
  // If you want to revert back, uncomment the code below and remove HeroSlider import
  // ========================================
  /*
  const HeroSection: React.FC = () => {
    const [showVideo, setShowVideo] = useState(false);
    const navigate=useNavigation

    return (
      <section className="hero" id="home">
        <div className="hero-decorative">
          <div className="circle circle1"></div>
          <div className="circle circle2"></div>
        </div>
        <div className="container">
          <motion.div
            className="experience-badge"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            6+ Years Experience
          </motion.div>

          <motion.div
            className="hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="hero-title">
              Empowering Businesses with{" "}
              <span className="gradient-text">
                Cutting-Edge Digital Solutions
              </span>
            </h1>
            <p className="hero-subtitle">
              From seamless electronic signatures to intelligent data
              extraction, we transform how you work, making your operations more
              efficient, secure, and future-ready.
            </p>

            <motion.div
              className="tech-icons-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <div className="tech-icon">🤖</div>
              <div className="tech-icon">📱</div>
              <div className="tech-icon">💻</div>
              <div className="tech-icon">🔐</div>
              <div className="tech-icon">☁️</div>
            </motion.div>

            <div className="hero-buttons">
              <button onClick={()=>openExternalLink(InfoService?.loginLink)} className="btn btn-primary">Get Started</button>
              <button
                className="btn btn-video"
                onClick={() => setShowVideo(true)}
              >
                <Play size={20} /> Watch Demo
              </button>
            </div>
          </motion.div>

          <AnimatePresence>
            {showVideo && (
              <motion.div
                className="video-modal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowVideo(false)}
              >
                <motion.div
                  className="video-container"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="close-video"
                    onClick={() => setShowVideo(false)}
                  >
                    <X size={24} />
                  </button>
                  <div className="video-wrapper">
                    <iframe
                      width="100%"
                      height="100%"
                      src="https://www.youtube.com/embed/4iRCVGMN1SA"
                      title="HDT Solutions Demo"
                      frameBorder="0"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    ></iframe>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    );
  };
  */
  // ======================================== End of Old HeroSection ========================================

  // Services Overview (Matching Brochure)
  const ServicesSection: React.FC = () => {
    const navigate = useNavigate();

    const services = [
      {
        icon: "💬",
        title: "WhatsApp Ordering & Restaurant OS",
        desc: "Customers order on WhatsApp, kitchen sees it instantly, stock auto-deducts, vendor invoices auto-parse. One number runs your whole restaurant.",
        features: "WhatsApp Cart • Kitchen Display • Recipe Inventory • AI Reports",
        detailPath: "/whatsapp-ordering",
        flagship: true,
      },
      {
        icon: "✍️",
        title: "Digital Signature Workflows",
        desc: "Streamline your document approval process with secure electronic signatures. Perfect for contracts, agreements, and legal documents.",
        features: "CRM Integration • Cloud-Based • Legally Binding",
        detailPath: "/digital-signature-solution",
      },
      {
        icon: "📄",
        title: "Document Automation",
        desc: "Automated PDF and Word template processing with merge, compress, and fill capabilities for enhanced productivity.",
        features: "Template Management • Batch Processing • Multi-format",
        detailPath: "/pdf-word-template-filling",
      },
      {
        icon: "🤖",
        title: "AI Data Extraction",
        desc: "Intelligent extraction of data from invoices, resumes, contracts with seamless integration to your existing systems.",
        features: "Resume Parsing • Invoice Processing • Smart OCR",
        detailPath: "/ai-data-extraction",

      },
      {
        icon: "💬",
        title: "Intelligent Chatbots",
        desc: "Custom chatbot solutions powered by LLM technology for customer service, support, and business automation.",
        features: "24/7 Support • Multi-language • Industry Specific",
        detailPath: "/intelligent-chatbot",
      },
      {
        icon: "🔒",
        title: "Security Solutions",
        desc: "Comprehensive content security policy implementation to protect your web applications from modern threats.",
        features: "CSP Implementation • Security Audits • Plugin Development",
        detailPath: "/content-security-policy",
      },
      {
        icon: "👥",
        title: "Employee Onboarding",
        desc: "Streamline employee onboarding with automated document collection, AI-powered data extraction, and integrated digital signatures.",
        features: "Document Collection • AI Extraction • Digital Signature",
        detailPath: "/employee-onboarding",
      },
      {
        icon: "🤝",
        title: "Client Onboarding",
        desc: "Accelerate client onboarding with intelligent document processing, template-based forms, and secure e-signature workflows.",
        features: "Smart Forms • Data Extraction • Secure Signing",
        detailPath: "/client-onboarding",
      },
      {
        icon: "🏢",
        title: "Vendor Onboarding",
        desc: "Simplify vendor registration with automated document verification, compliance checks, and digital contract signing.",
        features: "Document Verification • Compliance • E-Contracts",
        detailPath: "/vendor-onboarding",
      },
      {
        icon: "⭐",
        title: "Why Choose Us?",
        desc: "Industry expertise, proven track record, and commitment to innovation make us your ideal digital transformation partner.",
        features: "Trusted • Innovative • Results-Driven",
        special: true,
      },
    ];

    return (
      <section className="services" id="products">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">OUR SERVICES</h2>
            <p className="section-subtitle">
              Comprehensive Digital Solutions for Modern Businesses
            </p>
          </motion.div>

          <div className="services-grid">
            {services.map((service, idx) => (
              <motion.div
                key={idx}
                className={`service-card ${
                  service.special ? "service-special" : ""
                } ${service.flagship ? "service-flagship" : ""}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                whileHover={{ y: -10 }}
              >
                {service.flagship && (
                  <div className="service-flagship-ribbon">★ FLAGSHIP</div>
                )}
                <div className="service-icon">{service.icon}</div>
                <h3 className="service-title">{service.title}</h3>
                <p className="service-desc">{service.desc}</p>
                <div className="service-features">{service.features}</div>
                {!service.special && (
                  <div className="service-buttons">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => navigate(service.detailPath!)}
                    >
                      View More
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() =>
                        openExternalLink(
                          service.flagship
                            ? InfoService?.bookingLink
                            : InfoService?.loginLink + service.detailPath
                        )
                      }
                    >
                      Try Free
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Detailed Products Section
  const DetailedProducts: React.FC = () => {
    const navigate = useNavigate();
    const [showDetailedProductVideo, setShowDetailedProductVideo] = useState(false);
    const [currentDetailedVideoUrl, setCurrentDetailedVideoUrl] = useState("");

    const products = [
      {
        number: 1,
        title: "WhatsApp Ordering & Restaurant OS (HDT Booking)",
        desc: "Flagship product. Customers order on WhatsApp; your kitchen, stock, vendors, and AI reports stay in sync. Built for restaurants, bakeries, salons, grocery & clothing stores.",
        features: [
          "Conversational WhatsApp ordering",
          "Kitchen Display System (KDS / KOT)",
          "Recipe-linked stock & wastage closure",
          "Vendor module with PO + price-variance",
          "Gmail invoice auto-parse → WhatsApp approval",
          "AI Report Chat in plain English",
          "Multi-tenant, white-label & franchise wallet",
          "1-click migrate from Square / Clover",
        ],
        detailPath: "/whatsapp-ordering",
        videoUrl: homeVideo,
        flagship: true,
      },
      {
        number: 2,
        title: "Digital/Electronic Signature Workflow",
        desc: "Complete e-signature solution that allows users to upload agreements, mark signature fields, and send for electronic signing with full CRM integration.",
        features: [
          "Upload any document format",
          "Drag & drop signature fields",
          "Multi-party signing support",
          "CRM system integration",
          "Audit trail & compliance",
          "Mobile-friendly interface",
          "Automated reminders",
          "Bulk document processing",
        ],
        detailPath: "/digital-signature-solution",
        videoUrl: digitalVideo,
      },
      {
        number: 3,
        title: "PDF/Word Template Automation Services",
        desc: "Seamlessly integrate with any CRM application to automatically fill details in templates, merge documents, and compress files for optimal performance.",
        features: [
          "CRM data auto-population",
          "Template customization",
          "Document merging",
          "File compression",
          "Batch processing",
          "Multiple output formats",
          "Version control",
          "Cloud storage integration",
        ],
        detailPath: "/pdf-word-template-filling",
        videoUrl: templateVideo,
      },
      {
        number: 4,
        title: "AI-Powered Data Extraction",
        desc: "Advanced AI solutions for HR resume parsing and automated accounts payable processing. Extract structured data from scanned documents and email attachments.",
        features: [
          "Resume parsing & analysis",
          "Invoice data extraction",
          "Contract information mining",
          "Google Drive integration",
          "WhatsApp document processing",
          "OCR technology",
          "Multi-language support",
          "Real-time processing",
        ],
        detailPath: "/ai-data-extraction",
        videoUrl: extractVideo,
      },
      {
        number: 5,
        title: "Chatbot Creation & Integration",
        desc: "Custom intelligent chatbots for any industry including customer service, insurance, and specialized business processes. Features both rule-based and LLM-powered knowledge systems.",
        features: [
          "Industry-specific solutions",
          "Rule-based logic",
          "LLM integration",
          "Knowledge base management",
          "Multi-channel deployment",
          "Analytics & reporting",
          "Escalation handling",
          "Continuous learning",
        ],
        detailPath: "/intelligent-chatbot",
        videoUrl: testVideo,
      },
      {
        number: 6,
        title: "Content Security Policy Implementation",
        desc: "Comprehensive security solutions including CSP plugin development and implementation of strict security policies for enhanced website protection.",
        features: [
          "CSP policy creation",
          "Security audit services",
          "Plugin development",
          "XSS protection",
          "Code injection prevention",
          "Vulnerability assessment",
          "Security monitoring",
          "Compliance reporting",
        ],
        detailPath: "/content-security-policy",
        videoUrl: testVideo,
      },
      {
        number: 7,
        title: "Employee Onboarding Solution",
        desc: "Revolutionize employee onboarding with AI-powered document extraction, automated form filling, and integrated digital signatures. Collect documents, extract information, and complete paperwork in minutes.",
        features: [
          "Basic details collection form",
          "Document selection & upload",
          "AI-powered data extraction",
          "Template auto-fill from documents",
          "Digital & electronic signatures",
          "Security & data protection",
          "Consolidated data export",
          "Document archive & retrieval",
        ],
        detailPath: "/employee-onboarding",
        videoUrl: employeeVideo,
      },
      {
        number: 8,
        title: "Client Onboarding Platform",
        desc: "Fast-track client acquisition with intelligent document processing. Enter basic info, select required documents, and let AI extract and fill templates automatically with full digital signature integration.",
        features: [
          "Client information forms",
          "Required document checklist",
          "Template selection system",
          "AI document data extraction",
          "Automatic template population",
          "E-signature workflows",
          "Compliance & security controls",
          "Client data export with docs",
        ],
        detailPath: "/client-onboarding",
        videoUrl: testVideo,
      },
      {
        number: 9,
        title: "Vendor Onboarding System",
        desc: "Streamline vendor registration and approval with automated document verification, AI-powered data extraction, and digital signature integration. Ensure compliance and security throughout.",
        features: [
          "Vendor registration portal",
          "Document upload & verification",
          "AI-powered data capture",
          "Template-based agreements",
          "Digital contract signing",
          "Compliance validation",
          "Secure data management",
          "Vendor database with docs",
        ],
        detailPath: "/vendor-onboarding",
        videoUrl: vendorVideo,
      },
    ];

    return (
      <section className="detailed-products" id="products-detail">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">OUR PRODUCTS</h2>
            <p className="section-subtitle">
              Advanced tools for software solutions
            </p>
          </motion.div>

          {/* Video Showcase */}
          <motion.div
            className="products-video-section"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <VideoPlayer
              videoSrc={homeVideo}
              title="See Our Solutions in Action"
              description="Discover how our digital solutions transform businesses with cutting-edge technology. Watch real-world applications, success stories, and see how we help companies streamline operations and boost productivity"
              lightBackground={false}
            />
          </motion.div>

          <div className="products-list">
            {products.map((product, idx) => (
              <motion.div
                key={idx}
                className={`product-card ${product.flagship ? "product-flagship" : ""}`}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                {product.flagship && (
                  <div className="product-flagship-ribbon">★ FLAGSHIP</div>
                )}
                <div className="product-header">
                  <div className="product-number">{product.number}</div>
                  <h3 className="product-title">{product.title}</h3>
                </div>
                <p className="product-desc">{product.desc}</p>
                <div className="product-features">
                  {product.features.map((feature, fIdx) => (
                    <div key={fIdx} className="feature-item">
                      {feature}
                    </div>
                  ))}
                </div>
                <div className="product-buttons">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => navigate(product.detailPath)}
                  >
                    <Eye size={16} /> View More
                  </button>
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() =>
                      openExternalLink(
                        product.flagship
                          ? InfoService?.bookingLink
                          : InfoService?.loginLink + product.detailPath
                      )
                    }
                  >
                    <Rocket size={16} /> Try Free
                  </button>
                  <button
                    className="btn btn-video btn-sm"
                    onClick={() => {
                      setCurrentDetailedVideoUrl(product.videoUrl!);
                      setShowDetailedProductVideo(true);
                    }}
                  >
                    <Video size={16} /> Watch Demo
                  </button>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Video Modal for Detailed Products */}
          <AnimatePresence>
            {showDetailedProductVideo && (
              <motion.div
                className="video-modal"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setShowDetailedProductVideo(false)}
              >
                <motion.div
                  className="video-container"
                  initial={{ scale: 0.8 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0.8 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    className="close-video"
                    onClick={() => setShowDetailedProductVideo(false)}
                  >
                    <X size={24} />
                  </button>
                  <div className="video-wrapper">
                    <video
                      width="100%"
                      height="100%"
                      controls
                      autoPlay
                      src={currentDetailedVideoUrl}
                    >
                      Your browser does not support the video tag.
                    </video>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
    );
  };

  // Why Choose Us
  const WhyChooseUs: React.FC = () => {
    const features = [
      {
        icon: "⚡",
        title: "Fast Implementation",
        desc: "24-48 hour setup time",
      },
      {
        icon: "🔒",
        title: "Enterprise Security",
        desc: "Bank-level encryption",
      },
      { icon: "🎯", title: "Custom Solutions", desc: "Tailored to your needs" },
      {
        icon: "💰",
        title: "Cost Effective",
        desc: "Save up to 90% on operations",
      },
      { icon: "📈", title: "Scalable", desc: "Grows with your business" },
      { icon: "🆘", title: "24/7 Support", desc: "Always here to help" },
    ];

    return (
      <section className="why-choose" id="about">
        <div className="container">
          <motion.div
            className="section-header"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="section-title">Why Choose HDT Solutions?</h2>
            <p className="section-subtitle">
              Industry expertise with proven results
            </p>
          </motion.div>

          <div className="features-grid">
            {features.map((feature, idx) => (
              <motion.div
                key={idx}
                className="feature-card"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
              >
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-desc">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    );
  };

  // Staffing Section
  const StaffingSection: React.FC = () => {
    const navigate = useNavigate();

    const staffingHighlights = [
      { icon: "👨‍💻", text: "IT & Technical Staffing" },
      { icon: "⏱️", text: "Contract & Permanent Roles" },
      { icon: "🎯", text: "Executive Search" },
      { icon: "🌍", text: "Global Talent Pool" },
    ];

    return (
      <section className="staffing-section" id="staffing">
        <div className="container">
          <motion.div
            className="staffing-content"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="columns is-vcentered">
              <div className="column is-6">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="staffing-badge">
                    <Users size={20} /> Staffing Solutions
                  </div>
                  <h2 className="staffing-title">
                    Find the Right Talent for Your Team
                  </h2>
                  <p className="staffing-description">
                    Connect with top-tier professionals or discover your dream
                    job. Our comprehensive staffing solutions cover IT staffing,
                    contract placements, permanent hiring, and executive search
                    across all industries.
                  </p>

                  <div className="staffing-highlights">
                    {staffingHighlights.map((item, idx) => (
                      <motion.div
                        key={idx}
                        className="staffing-highlight-item"
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.3 + idx * 0.1 }}
                      >
                        <span className="highlight-icon">{item.icon}</span>
                        <span className="highlight-text">{item.text}</span>
                      </motion.div>
                    ))}
                  </div>

                  <div className="staffing-actions">
                    <button
                      className="btn btn-primary"
                      onClick={() => navigate("/services/staffing-solutions")}
                    >
                      <Briefcase size={20} /> Hire Talent
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => navigate("/services/staffing-solutions")}
                    >
                      <ArrowRight size={20} /> Submit Resume
                    </button>
                  </div>

                  <button
                    className="staffing-view-more"
                    onClick={() => navigate("/services/staffing-solutions")}
                  >
                    View More <ArrowRight size={16} />
                  </button>
                </motion.div>
              </div>

              <div className="column is-6">
                <motion.div
                  className="staffing-stats-card"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="stat-item-large">
                    <span className="stat-number-large">5,000+</span>
                    <span className="stat-label-large">
                      Successful Placements
                    </span>
                  </div>
                  <div className="stat-item-large">
                    <span className="stat-number-large">1,000+</span>
                    <span className="stat-label-large">Partner Companies</span>
                  </div>
                  <div className="stat-item-large">
                    <span className="stat-number-large">15+</span>
                    <span className="stat-label-large">Industries Served</span>
                  </div>
                  <div className="stat-item-large">
                    <span className="stat-number-large">98%</span>
                    <span className="stat-label-large">Match Success Rate</span>
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  // CTA Section
  const CTASection: React.FC = () => {
    let navigate=useNavigate();
    return (
      <section className="cta" id="contact">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="cta-title">Ready to Transform Your Business?</h2>
            <p className="cta-text">
              Get started today with a free consultation. Let's discuss how our
              solutions can help you grow.
            </p>
            <div className="cta-buttons">
              <button className="btn btn-primary">
                Schedule Free Consultation
              </button>
              <button className="btn btn-secondary" onClick={()=>navigate("/pricing")}>View Pricing</button>
            </div>
          </motion.div>
        </div>
      </section>
    );
  };

  return (
    <main>
      <HeroSlider />
      <BookingShowcase />

      <ServicesSection />
      <DetailedProducts />
      <StaffingSection />
      <WhyChooseUs />
      <CTASection />
    </main>
  );
};

export default HomePage;
